/**
 * Query parameter parser and validator.
 */
import {
  isNonEmptyString,
  isValidAmount,
  isValidCurrency,
  isValidEmail,
  sanitizeString,
} from './validators';

export interface CheckoutParams {
  profileId: string;
  orderId: string;
  amount: string;
  currency: string;
  customerName: string;
  customerEmail: string;
}

export interface ParseResult {
  params: CheckoutParams | null;
  errors: string[];
}

interface FieldValidator {
  validate: (value: unknown) => boolean;
  message: string;
}

const FIELD_VALIDATORS: Record<string, FieldValidator> = {
  profileId: {
    validate: isNonEmptyString,
    message: 'Profile ID is required.',
  },
  orderId: {
    validate: isNonEmptyString,
    message: 'Order ID is required.',
  },
  amount: {
    validate: isValidAmount,
    message: 'Amount must be a positive number with up to 2 decimal places.',
  },
  currency: {
    validate: isValidCurrency,
    message: 'Currency must be one of: CAD, USD.',
  },
};

const OPTIONAL_FIELD_VALIDATORS: Record<string, FieldValidator> = {
  customerEmail: {
    validate: isValidEmail,
    message: 'Customer email format is invalid.',
  },
};

export function parseCheckoutParams(searchParams: URLSearchParams): ParseResult {
  const errors: string[] = [];

  const raw = extractRawParams(searchParams);

  for (const [field, rule] of Object.entries(FIELD_VALIDATORS)) {
    if (!rule.validate(raw[field as keyof typeof raw])) {
      errors.push(rule.message);
    }
  }

  for (const [field, rule] of Object.entries(OPTIONAL_FIELD_VALIDATORS)) {
    const val = raw[field as keyof typeof raw];
    if (val && !rule.validate(val)) {
      errors.push(rule.message);
    }
  }

  if (errors.length > 0) {
    return { params: null, errors };
  }

  const params: CheckoutParams = {
    profileId: sanitizeString(raw.profileId),
    orderId: sanitizeString(raw.orderId),
    amount: raw.amount.trim(),
    currency: raw.currency.toUpperCase().trim(),
    customerName: raw.customerName ? sanitizeString(raw.customerName) : '',
    customerEmail: raw.customerEmail ? sanitizeString(raw.customerEmail) : '',
  };

  return { params, errors: [] };
}

function extractRawParams(searchParams: URLSearchParams) {
  const lowerMap: Record<string, string> = {};
  for (const [key, value] of searchParams.entries()) {
    lowerMap[key.toLowerCase()] = value;
  }

  return {
    profileId: lowerMap['profileid'] || '',
    orderId: lowerMap['orderid'] || '',
    amount: lowerMap['amount'] || '',
    currency: lowerMap['currency'] || '',
    customerName: lowerMap['customername'] || '',
    customerEmail: lowerMap['customeremail'] || '',
  };
}
